/**
 * Data for the past-meeting screens.
 *
 * What the server keeps about a meeting once it is over: its record (title,
 * times, actual run time — the meetings store has it), its recordings, its
 * group chat and its transcript. Those come from their APIs here. Who
 * attended and for how long is not recorded anywhere yet, so it comes back
 * empty rather than invented.
 *
 * This file used to return one made-up meeting — "Q3 Sprint Retrospective",
 * stock-photo participants, recordings linking to "#", a scripted chat and
 * transcript — for every past meeting, whatever its id.
 */

import {
  absolutizeRecordingUrl,
  listRecordings,
  type Recording,
} from '@/features/meeting/services/recordings.api'
import { heldPlayback, type HeldPlayback } from '@/features/meeting/services/recording-link'
import { meetingsApi, type MeetingChatHistoryMessage, type MeetingTranscriptSegment } from './meetings.api'
import { formatMeetingTime } from './meetings.service'

export interface PastMeetingParticipant {
  id: string
  name: string
  email: string
  imageUrl?: string
  joinTime: string
  leaveTime: string
  durationMin: number
  isHost: boolean
  audioOn: boolean
  videoOn: boolean
}

export interface PastMeetingRecording {
  id: string
  name: string
  /** Playback URL as listed. A private recording's lapses: open it through
   *  recording-link.ts with `playback`, never this directly. */
  url: string
  /** The playback URL with when it lapses, for recording-link.ts. */
  playback: HeldPlayback
  duration: string
  size: string
  /** Empty when the mixer produced no still. */
  thumbnail: string
}

export interface PastMeetingChatMessage {
  id: string
  /** Empty for a sender the server has no name for. */
  sender: string
  text: string
  time: string
  attachment: { url: string; name: string } | null
}

export interface PastMeetingTranscriptEntry {
  speaker: string
  time: string
  text: string
}

/** What the server holds about a past meeting besides its record. */
export interface PastMeetingDetail {
  /** Not recorded yet: always empty. */
  participants: PastMeetingParticipant[]
  recordings: PastMeetingRecording[]
  chatMessages: PastMeetingChatMessage[]
  transcript: PastMeetingTranscriptEntry[]
  /** Whether anyone's speech was transcribed in this meeting. */
  transcriptionAvailable: boolean
}

export type AttendanceStatus = 'present' | 'late' | 'absent'

export interface AttendanceTimelineRecord {
  id: string
  name: string
  email: string
  imageUrl?: string
  joinTime: string
  leaveTime: string
  durationMin: number
  meetingDurationMin: number
  status: AttendanceStatus
  audioUsed: boolean
  videoUsed: boolean
  joinTimestamp: number
  leaveTimestamp: number
}

export interface AttendanceTimeline {
  meetingDurationMin: number
  meetingStartLabel: string
  meetingEndLabel: string
  attendees: AttendanceTimelineRecord[]
}

/** `4503` -> `1:15:03`, `134` -> `2:14`. */
function formatDuration(seconds: number | null): string {
  if (!seconds || seconds < 0) return ''
  const s = Math.round(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

/** `358612992` -> `342 MB`. */
function formatSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.max(1, Math.round(mb))} MB`
}

function toRecording(r: Recording): PastMeetingRecording | null {
  // Only a finished upload has a playback URL; one still recording or
  // uploading, or one that failed, has nothing to play yet.
  const url = absolutizeRecordingUrl(r.playbackUrl)
  if (r.status !== 'ready' || !url) return null
  const startedMs = new Date(r.startedAt ?? r.createdAt).getTime()
  return {
    id: r.id,
    // The mixer records a room, not a titled document, so the start time is the
    // honest label (the Recordings page does the same).
    name: formatMeetingTime(startedMs),
    url,
    playback: heldPlayback(r),
    duration: formatDuration(r.durationSeconds),
    size: formatSize(r.bytes),
    thumbnail: absolutizeRecordingUrl(r.thumbnailUrl) ?? '',
  }
}

function toChatMessage(m: MeetingChatHistoryMessage): PastMeetingChatMessage {
  return {
    id: m.id,
    sender: m.fromName ?? '',
    text: m.text,
    time: formatMeetingTime(m.ts),
    attachment: m.attachment ? { url: m.attachment.url, name: m.attachment.name } : null,
  }
}

function toTranscriptEntry(s: MeetingTranscriptSegment): PastMeetingTranscriptEntry {
  return {
    speaker: s.speakerName ?? '',
    time: formatMeetingTime(s.ts),
    text: s.text,
  }
}

export const meetingHistoryRepo = {
  /**
   * Recordings, group chat and transcript of a past meeting, by its id.
   *
   * Each is loaded on its own and comes back empty if its request fails: the
   * server lists recordings only to whoever started them or hosts the meeting,
   * and refuses chat and transcript to someone who neither hosted nor
   * attended, so a failure is often a plain "not yours to see" rather than an
   * outage.
   */
  async getPastMeeting(meetingId: string): Promise<PastMeetingDetail> {
    const [recordings, chat, transcript] = await Promise.allSettled([
      listRecordings({ meetingId, limit: 50 }),
      meetingsApi.getChatHistory(meetingId),
      meetingsApi.getTranscripts(meetingId),
    ])
    const transcriptEntries =
      transcript.status === 'fulfilled' ? transcript.value.transcripts.map(toTranscriptEntry) : []
    return {
      participants: [],
      recordings:
        recordings.status === 'fulfilled'
          ? recordings.value.recordings.map(toRecording).filter((r): r is PastMeetingRecording => r !== null)
          : [],
      chatMessages: chat.status === 'fulfilled' ? chat.value.messages.map(toChatMessage) : [],
      transcript: transcriptEntries,
      // A meeting held before this feature shipped, or one with no speech,
      // looks the same as a fetch failure here — all three have nothing to
      // show, and the UI's empty state already covers that.
      transcriptionAvailable: transcriptEntries.length > 0,
    }
  },
  /** Attendance is not recorded yet, so there is no timeline to show. */
  async getAttendanceTimeline(): Promise<AttendanceTimeline> {
    return { meetingDurationMin: 0, meetingStartLabel: '', meetingEndLabel: '', attendees: [] }
  },
}
